import { useRef, useState } from "react";
import { UploadCloud, X, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";

export const ImageUploader = ({ images, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        urls.push(data.url);
      }
      onChange([...images, ...urls]);
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          uploadFiles(e.dataTransfer.files);
        }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed py-10 text-center transition-colors ${
          drag ? "border-zinc-900 bg-zinc-50" : "border-zinc-300 hover:border-zinc-400"
        }`}
        data-testid="image-dropzone"
      >
        {uploading ? (
          <Loader2 size={26} className="animate-spin text-kkaccent" />
        ) : (
          <UploadCloud size={26} className="text-zinc-400" />
        )}
        <p className="text-sm text-zinc-600">
          <span className="font-medium text-zinc-900">Click to upload</span> or drag &amp; drop
        </p>
        <p className="text-xs text-zinc-400">PNG, JPG, WEBP — multiple allowed</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="image-input"
        />
      </div>

      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
          {images.map((img, i) => (
            <div key={i} className="group relative aspect-square overflow-hidden border border-zinc-200 bg-zinc-100" data-testid={`uploaded-image-${i}`}>
              <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />
              {i === 0 && (
                <span className="absolute left-1 top-1 bg-zinc-900 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white">Main</span>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-white/90 text-zinc-700 opacity-0 transition-opacity group-hover:opacity-100"
                data-testid={`remove-image-${i}`}
              >
                <X size={13} />
              </button>
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                <button type="button" onClick={() => move(i, -1)} className="p-1 text-white"><ArrowLeft size={13} /></button>
                <button type="button" onClick={() => move(i, 1)} className="p-1 text-white"><ArrowRight size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
